import { isEmpty } from '@fruitful/fruitils';
import { router } from 'expo-router';
import * as React from 'react';
import { useState } from 'react';
import {
    Animations,
    ArrowRightIcon,
    BodyMediumEmphasized,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    H2Regular,
    ListChecksIcon,
    NavigationTile,
    Separator,
    Sheet,
    SmoothieScrollView,
    Spinner,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import TabHeader from '../../common/components/TabHeader';
import { IS_PRODUCTION } from '../../config/FruitfulEnvironment';
import { useTrackViewOnFocus } from '../../events/hooks/useTrackViewOnFocus';
import GoalsSummary from '../../goals/components/summary/GoalsSummary';
import { useGetMoneyMapForMembership } from '../hooks/useGetMoneyMapsForMembership';
import { useMoneyMapWorkflow } from '../hooks/useMoneyMapWorkflow';
import {
    MoneyMapResetState,
    useResetMoneyMap,
} from '../hooks/useResetMoneyMap';
import { useRoadmapFeature } from '../hooks/useRoadmapFeature';
import { RoadmapMoneyMapSection } from './RoadmapMoneyMapSection';

const TasksTile = () => {
    return (
        <NavigationTile
            title="Goals & Tasks"
            description="Review your tasks & to-do's to prepare for upcoming sessions with your Guide"
            backgroundColor="$peach300"
            iconLeft={ListChecksIcon}
            iconRight={ArrowRightIcon}
            onPress={() => {
                router.navigate('/(app)/roadmap/tasks');
            }}
        />
    );
};

const RoadmapDevToolsButton = () => {
    const [isOpen, setIsOpen] = useState(false);

    if (IS_PRODUCTION) {
        return null;
    }

    return (
        <>
            <XStack
                gap={8}
                position="absolute"
                bottom={20}
                right={20}
                zIndex={999}
            >
                <View
                    onPress={() => {
                        setIsOpen(true);
                    }}
                    cursor="pointer"
                    backgroundColor="$gray200"
                    borderRadius={100}
                    width={40}
                    height={40}
                    alignItems="center"
                    justifyContent="center"
                    accessibilityRole="button"
                    accessibilityLabel="Open Roadmap dev tools"
                >
                    <H2Regular>🛠️</H2Regular>
                </View>
            </XStack>
            <RoadmapDevToolsSheet
                isOpen={isOpen}
                onClose={() => {
                    setIsOpen(false);
                }}
            />
        </>
    );
};

const RoadmapDevToolsSheet = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) => {
    const { isLoadingMoneyMap, moneyMap, refetchMoneyMapCompleteness } =
        useMoneyMapWorkflow();
    const { mutateAsync: resetMoneyMap } = useResetMoneyMap();
    const toast = useToastController();

    const resetSubmit = async (resetState: MoneyMapResetState) => {
        toast.show(`Reset starting...`);
        await resetMoneyMap({
            body: {
                moneyMapId: moneyMap?.id ?? '',
                resetState,
            },
        });
        toast.show(`Reset Complete!`);

        // TODO disable button while running reset
        // and figure out the right way to trigger
        // the entire page to reset
        void refetchMoneyMapCompleteness();
    };

    return (
        <Sheet isOpen={isOpen} onClose={onClose} showCloseButton={true}>
            <SmoothieScrollView>
                <YStack alignItems="center" gap={16} paddingBottom={40}>
                    <YStack alignItems="center" gap={4}>
                        <BodyMediumEmphasized>
                            Money Map Development Tools
                        </BodyMediumEmphasized>
                    </YStack>

                    {/* Shortcuts Section */}
                    <YStack gap={12} width="100%" maxWidth={400}>
                        <Separator marginTop={16} />
                        <BodyMediumEmphasized>
                            Shortcuts ⏩️
                        </BodyMediumEmphasized>
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Start"
                            onPress={() => {
                                onClose();
                                router.navigate('/roadmap/go/start');
                            }}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Money Map"
                            onPress={() => {
                                onClose();
                                router.navigate('/roadmap/go/money-map');
                            }}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Open cash accounts"
                            onPress={() => {
                                onClose();
                                router.navigate('/roadmap/go/cash-accounts');
                            }}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Open card accounts"
                            onPress={() => {
                                onClose();
                                router.navigate('/roadmap/go/card-accounts');
                            }}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Open investment accounts"
                            onPress={() => {
                                onClose();
                                router.navigate(
                                    '/roadmap/go/investment-accounts',
                                );
                            }}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Income Split"
                            onPress={() => {
                                onClose();
                                router.navigate('/roadmap/go/income-split');
                            }}
                        />
                    </YStack>

                    {/* Reset Section */}
                    <YStack gap={12} width="100%" maxWidth={400}>
                        <Separator marginTop={16} />
                        <BodyMediumEmphasized>Reset 🔄</BodyMediumEmphasized>
                        {isLoadingMoneyMap || moneyMap == null ? (
                            <Spinner alignSelf="center" />
                        ) : (
                            <>
                                <Button
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.SMALL}
                                    text="Reset Income Split"
                                    onPress={() => {
                                        void resetSubmit(
                                            MoneyMapResetState.RESET_INCOME_SPLIT,
                                        );
                                    }}
                                />
                                <Button
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.SMALL}
                                    text="Reset Opening IM Accounts + above"
                                    onPress={() => {
                                        void resetSubmit(
                                            MoneyMapResetState.RESET_OPENING_APEX_ACCOUNTS,
                                        );
                                    }}
                                />
                                <Button
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.SMALL}
                                    text="Reset Opening Accounts + above"
                                    onPress={() => {
                                        void resetSubmit(
                                            MoneyMapResetState.RESET_OPENING_ACCOUNTS,
                                        );
                                    }}
                                />
                                <Button
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.SMALL}
                                    text="Reset Money Map Review + above"
                                    disabled={true}
                                    onPress={() => {
                                        // Dev-only placeholder; roadmap reset UX is tracked elsewhere
                                        // eslint-disable-next-line no-alert -- debug control in dev tools
                                        alert(
                                            'money map review is not currently tracked; reset account opening',
                                        );
                                    }}
                                />
                                <Button
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.SMALL}
                                    text="Reset Money Map to Draft + above"
                                    onPress={() => {
                                        void resetSubmit(
                                            MoneyMapResetState.RESET_MONEY_MAP_STATUS_TO_DRAFT,
                                        );
                                    }}
                                />
                            </>
                        )}
                    </YStack>
                </YStack>
            </SmoothieScrollView>
        </Sheet>
    );
};

const Roadmap = () => {
    const { disableRoadmapFeatures } = useRoadmapFeature();
    const { data: moneyMap, isLoading } = useGetMoneyMapForMembership();
    useTrackViewOnFocus(
        disableRoadmapFeatures ? 'RoadmapGoalsSummary' : 'Roadmap',
    );

    if (disableRoadmapFeatures) {
        return <GoalsSummary includeTabHeader={true} />;
    }

    if (isLoading) {
        return <FullScreenSpinner />;
    }

    const hasMoneyMap = !isEmpty(moneyMap);

    return (
        <View flex={1}>
            <TabHeader headerTitle="Money Map" />
            <RoadmapDevToolsButton />
            <SmoothieScrollView
                containerStyle={{
                    marginHorizontal: 24,
                    $sm: { width: '100%', marginHorizontal: 0 },
                }}
            >
                <YStack
                    gap={24}
                    marginBottom={50}
                    $sm={{
                        paddingHorizontal: 36,
                        // These styles are good if the components are relatively similar in height
                        // flexDirection: hasMoneyMap ? 'row' : 'row-reverse',
                        // flexWrap: hasMoneyMap ? 'wrap' : 'wrap-reverse',
                        // alignItems: hasMoneyMap ? 'flex-start' : 'flex-end',
                    }}
                    width="100%"
                    maxWidth={1100}
                    alignSelf="center"
                    alignItems="center"
                    flexDirection={hasMoneyMap ? 'column' : 'column-reverse'}
                >
                    <RoadmapMoneyMapSection />
                    <YStack
                        width="100%"
                        maxWidth={500}
                        {...Animations.FadeInFromAbove}
                    >
                        <TasksTile />
                    </YStack>
                </YStack>
            </SmoothieScrollView>
        </View>
    );
};

export default Roadmap;
